import React, { useState } from 'react'
import { v1 as uuid } from 'uuid'
import withStyles from '@mui/styles/withStyles'
import { Typography, Button, CircularProgress } from '@mui/material'
import produce from 'immer'

import { useUpdateEffect } from 'genesis-suite/hooks'
import PropertySelectorController from './PropertySelectorController'
import ValueSelectorController from './ValueSelectorController'
import ModuleSecurityController from './ModuleSecurityController'
import NodePermissionController from './NodePermissionController'
import NetworkPermissionController from './NetworkPermissionController'

const styles = theme => ({
    container: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        flexGrow: 1,
    },
    divider: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    section: {
        margin: theme.spacing(4, 0),
    },
    controls: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    buttonProgress: {
        color: theme.palette.primary.main,
    },
})

const formatData = (data, type) => {
    return data.reduce((acc, item) => {
        if (item.properties && item.properties.length > 0) {
            const indexProperty = item.properties.find(prop => prop.key)
            acc.push({
                ...item,
                option: { type, label: item.name, value: item.id },
                propertyOptions: item.properties.map(prop => ({
                    label: prop.displayName,
                    value: prop.id,
                    key: prop.key,
                })),
                indexProperty,
            })
        }
        return acc
    }, [])
}

const SecurityFilters = ({ classes, architecture, user: defaultUser, context, isSubmitting, onChange, onSave }) => {
    const [user, setUser] = useState(defaultUser)

    useUpdateEffect(() => {
        onChange(user)
    }, [user])

    if (!defaultUser) return null

    const { nodes, links } = architecture
    const formattedNodes = nodes ? formatData(nodes, 'node') : []
    const formattedLinks = links ? formatData(links, 'link') : []
    const allOptions = [...formattedNodes, ...formattedLinks]
    const nodeOptions = nodes?.length ? nodes.map(node => ({ label: node.name, value: node.id, type: 'node' })) : []
    const linkOptions = links?.length ? links.map(link => ({ label: link.name, value: link.id, type: 'link' })) : []

    const blockedNodeIds = user.nodePermissions ? Object.keys(user.nodePermissions) : []
    const blockedProperties = defaultUser.propertyPermissions ? Object.keys(defaultUser.propertyPermissions) : []
    const blockedNodes = blockedNodeIds.map(id => nodeOptions.find(nodeOpt => nodeOpt.value === id))

    const propSelectorObj = blockedProperties.reduce((acc, propId) => {
        const element = allOptions.find(e => e.properties.some(p => p.id === propId))
        if (element) {
            const propOp = element.propertyOptions.find(propOpt => propOpt.value === propId)
            const { [element.id]: { properties } = {} } = acc //existing properties for this element in the accumulator, if any

            acc = {
                ...acc,
                [element.id]: {
                    id: uuid(),
                    node: element.option,
                    properties: properties ? [...properties, propOp] : [propOp],
                },
            }
        }
        return acc
    }, {})
    const propSelectorRows = Object.values(propSelectorObj)

    const contextFilters = defaultUser.securityFilters || []
    const contextFilterRows = contextFilters.map(contextFilter => {
        const { values, resourceName, propertyName } = contextFilter
        const node = formattedNodes.find(n => n.name === resourceName)
        const property = node.properties.find(p => p.name === propertyName)
        return {
            id: uuid(),
            node: node.id,
            property: property.id,
            values: values,
        }
    })

    const modulePermissions = defaultUser.portalsPermissions || {}
    const nodePermissions = defaultUser.nodePermissions || {}

    const moduleSecurityRows = Object.keys(modulePermissions).map(moduleName => ({
        id: uuid(),
        node: moduleName,
        role: modulePermissions[moduleName],
    }))

    const nodePermissionsRows = Object.keys(nodePermissions).map(node => ({
        id: uuid(),
        node: node,
        role: nodePermissions[node],
    }))

    const netWorkSecurityRows = [{ id: uuid() }]

    const handlePropPermChange = rows => {
        setUser(
            produce(user, draft => {
                draft.propertyPermissions = rows.reduce((acc, row) => {
                    if (row.properties) {
                        row.properties.forEach(prop => {
                            acc[prop.value] = 0
                        })
                    }
                    return acc
                }, {})
            })
        )
    }

    const handleContextFilterChange = rows => {
        setUser(
            produce(user, draft => {
                draft.securityFilters = rows.reduce((acc, row) => {
                    if (row.values) {
                        const node = nodes.find(node => row.node === node.id)
                        if (node) {
                            const propertyName = node.properties.find(prop => prop.id === row.property).name
                            acc.push({
                                filterName: null,
                                id: row.property,
                                propertyName,
                                resourceName: node.name,
                                resourceType: 'concept',
                                values: row.values,
                            })
                        }
                    }
                    return acc
                }, [])
            })
        )
    }

    const handleModuleSecurityChange = rows => {
        setUser(
            produce(user, draft => {
                draft.portalsPermissions = rows.reduce(
                    (acc, row) => ({
                        ...acc,
                        [row.node]: row.role,
                    }),
                    {}
                )
            })
        )
    }
    const handleNodeSecuityChange = rows => {
        const dta = rows.reduce(
            (acc, row) => ({
                ...acc,
                [row.node]: row.role,
            }),
            {}
        )
        setUser(
            produce(user, draft => {
                draft.nodePermissions = dta
            })
        )
    }
    const handleNetworkChange = rows => {
        setUser(
            produce(user, draft => {
                draft.contextDefaultNetworkFilters = { [rows[0].defaultNode]: rows[0].role }
            })
        )
    }

    return (
        <div className={classes.container}>
            <div>
                <div className={classes.section}>
                    <Typography variant="h6">Default Network Filter</Typography>
                    <NetworkPermissionController
                        context={context}
                        defaultRows={netWorkSecurityRows}
                        onChange={handleNetworkChange}
                        selected={user.contextDefaultNetworkFilters}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant="h6">Node Permissions</Typography>
                    <NodePermissionController
                        context={context}
                        options={nodeOptions}
                        defaultRows={nodePermissionsRows}
                        onChange={handleNodeSecuityChange}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant="h6">Blocked Properties</Typography>
                    <PropertySelectorController
                        defaultRows={propSelectorRows}
                        elementOptions={[...nodeOptions, ...linkOptions]}
                        allOptions={allOptions}
                        onChange={handlePropPermChange}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant="h6">Security Filters</Typography>
                    <ValueSelectorController
                        context={context}
                        defaultRows={contextFilterRows || []}
                        nodeOptions={nodeOptions}
                        allOptions={allOptions}
                        onChange={handleContextFilterChange}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant="h6">Portal Security</Typography>
                    <ModuleSecurityController
                        context={context}
                        defaultRows={moduleSecurityRows}
                        onChange={handleModuleSecurityChange}
                    />
                </div>
            </div>
            <div className={classes.controls}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => onSave(user)}
                    disabled={isSubmitting || user === defaultUser}
                >
                    {isSubmitting ? <CircularProgress size={24} className={classes.buttonProgress} /> : 'Save'}
                </Button>
            </div>
        </div>
    )
}

export default withStyles(styles)(SecurityFilters)
